
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminGeoPromotions, GeoPromotionFormData } from '@/hooks/useAdminGeoPromotions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  MapPin, Plus, Pencil, Trash2, Printer, Download, FileSpreadsheet,
  Target, Calendar, Radio, Megaphone
} from 'lucide-react';
import GeoPromotionLocationList from './GeoPromotionLocationList';
import { format } from 'date-fns';
import { exportToCSV, exportToExcel, formatDateForExport } from '@/utils/exportUtils';
import { toast } from 'sonner';
import type { ExportColumn } from '@/utils/exportUtils';

const PROMO_TYPES = ['discount', 'event', 'new_product', 'flash_sale'];
const SPECIES_OPTIONS = ['dog', 'cat', 'bird', 'reptile', 'fish', 'rabbit', 'hamster', 'horse'];

const emptyForm: GeoPromotionFormData = {
  business_name: '',
  title: '',
  description: '',
  promo_type: 'discount',
  discount_value: undefined,
  discount_unit: 'percent',
  latitude: 0,
  longitude: 0,
  radius_meters: 2000,
  starts_at: '',
  ends_at: '',
  is_active: true,
  max_notifications: undefined,
  target_species: [],
  locations: [{ latitude: 0, longitude: 0, radius_meters: 2000, label: '' }],
};

const AdminGeoPromotions: React.FC = () => {
  const { t } = useTranslation();
  const {
    promotions, impressionStats, isLoading,
    createPromotion, updatePromotion, toggleActive, deletePromotion,
    isCreating, isUpdating, isDeleting,
  } = useAdminGeoPromotions();

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<GeoPromotionFormData>({ ...emptyForm });

  const getStatus = (promo: any) => {
    if (!promo.is_active) return 'paused';
    const now = new Date();
    if (new Date(promo.ends_at) < now) return 'expired';
    if (new Date(promo.starts_at) > now) return 'scheduled';
    return 'active';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active': return <Badge className="bg-green-500/20 text-green-700 border-green-300">{t('geoPromotions.statusActive')}</Badge>;
      case 'paused': return <Badge variant="secondary">{t('geoPromotions.statusPaused')}</Badge>;
      case 'expired': return <Badge variant="outline" className="text-muted-foreground">{t('geoPromotions.statusExpired')}</Badge>;
      case 'scheduled': return <Badge className="bg-blue-500/20 text-blue-700 border-blue-300">{t('geoPromotions.statusScheduled')}</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleEdit = (promo: any) => {
    setEditId(promo.id);
    const locations = promo.locations && promo.locations.length > 0
      ? promo.locations.map((loc: any) => ({
          latitude: Number(loc.latitude),
          longitude: Number(loc.longitude),
          radius_meters: loc.radius_meters,
          label: loc.label || '',
        }))
      : [{ latitude: Number(promo.latitude), longitude: Number(promo.longitude), radius_meters: promo.radius_meters, label: '' }];
    setForm({
      business_name: promo.business_name,
      title: promo.title,
      description: promo.description || '',
      promo_type: promo.promo_type,
      discount_value: promo.discount_value || undefined,
      discount_unit: promo.discount_unit || 'percent',
      latitude: Number(promo.latitude),
      longitude: Number(promo.longitude),
      radius_meters: promo.radius_meters,
      starts_at: promo.starts_at?.slice(0, 16) || '',
      ends_at: promo.ends_at?.slice(0, 16) || '',
      is_active: promo.is_active,
      max_notifications: promo.max_notifications || undefined,
      target_species: promo.target_species || [],
      provider_id: promo.provider_id || undefined,
      locations,
    });
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!form.business_name || !form.title || !form.starts_at || !form.ends_at) {
      toast.error(t('geoPromotions.fillRequired'));
      return;
    }
    const hasValidLocation = form.locations.some(loc => loc.latitude !== 0 && loc.longitude !== 0);
    if (!hasValidLocation) {
      toast.error(t('geoPromotions.locationRequired', 'At least one location must be set'));
      return;
    }
    if (editId) {
      updatePromotion({ ...form, id: editId } as any);
    } else {
      createPromotion(form);
    }
    setShowForm(false);
    setEditId(null);
    setForm({ ...emptyForm });
  };

  const handleDelete = (id: string) => {
    if (confirm(t('geoPromotions.confirmDelete'))) {
      deletePromotion(id);
    }
  };

  const handleSpeciesToggle = (species: string) => {
    setForm(prev => ({
      ...prev,
      target_species: prev.target_species.includes(species)
        ? prev.target_species.filter(s => s !== species)
        : [...prev.target_species, species],
    }));
  };

  // Export columns
  const getExportColumns = (): ExportColumn<any>[] => [
    { key: 'title', header: t('geoPromotions.title') },
    { key: 'promo_type', header: t('geoPromotions.type') },
    { key: 'business_name', header: t('geoPromotions.business') },
    { key: 'discount_value', header: t('geoPromotions.discount'), formatter: (v: any, row: any) => v ? `${v}${row.discount_unit === 'percent' ? '%' : ''}` : '' },
    { key: 'radius_meters', header: t('geoPromotions.radius'), formatter: (v: any) => v ? `${v}m` : '' },
    { key: 'starts_at', header: t('geoPromotions.startDate'), formatter: (v: any) => formatDateForExport(v) },
    { key: 'ends_at', header: t('geoPromotions.endDate'), formatter: (v: any) => formatDateForExport(v) },
    { key: 'is_active', header: t('geoPromotions.status'), formatter: (_: any, row: any) => getStatus(row) },
    { key: 'current_notifications', header: t('geoPromotions.notificationsSent') },
    { key: 'id', header: t('geoPromotions.clicks'), formatter: (_: any, row: any) => String(impressionStats[row.id]?.clicked || 0) },
  ];

  const handleExportCSV = () => {
    exportToCSV(promotions, getExportColumns(), 'geo_promotions_report');
    toast.success(t('geoPromotions.exportSuccess'));
  };

  const handleExportExcel = async () => {
    await exportToExcel(promotions, getExportColumns(), 'geo_promotions_report', t('geoPromotions.reportTitle'));
    toast.success(t('geoPromotions.exportSuccess'));
  };

  const handlePrint = () => window.print();

  // Stats
  const activeCount = promotions.filter(p => getStatus(p) === 'active').length;
  const totalImpressions = promotions.reduce((sum, p) => sum + (p.current_notifications || 0), 0);
  const totalClicks = Object.values(impressionStats).reduce((sum, s) => sum + s.clicked, 0);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Print Header */}
      <div className="hidden print:block print:mb-6">
        <h1 className="text-2xl font-bold">{t('geoPromotions.reportTitle')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('geoPromotions.generatedOn')}: {format(new Date(), 'PPP p')}
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 print:grid-cols-3">
        <Card className="print:shadow-none print:border">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center">
                <Megaphone className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('geoPromotions.activePromotions')}</p>
                <p className="text-2xl font-bold">{activeCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="print:shadow-none print:border">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Target className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('geoPromotions.totalImpressions')}</p>
                <p className="text-2xl font-bold">{totalImpressions}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="print:shadow-none print:border">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                <Radio className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('geoPromotions.totalClicks')}</p>
                <p className="text-2xl font-bold">{totalClicks}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap gap-2 print:hidden">
        <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) { setEditId(null); setForm({ ...emptyForm }); } }}>
          <DialogTrigger asChild>
            <Button><Plus className="h-4 w-4 mr-2" />{t('geoPromotions.create')}</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editId ? t('geoPromotions.editPromotion') : t('geoPromotions.createPromotion')}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>{t('geoPromotions.title')} *</Label>
                <Input value={form.title} onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))} />
              </div>
              <div className="md:col-span-2">
                <Label>{t('geoPromotions.business')} *</Label>
                <Input value={form.business_name} onChange={e => setForm(prev => ({ ...prev, business_name: e.target.value }))} placeholder="PetStore Mall del Sur" />
              </div>
              <div className="md:col-span-2">
                <Label>{t('geoPromotions.description')}</Label>
                <Input value={form.description || ''} onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))} />
              </div>
              <div>
                <Label>{t('geoPromotions.type')}</Label>
                <Select value={form.promo_type} onValueChange={v => setForm(prev => ({ ...prev, promo_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROMO_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{t(`geoPromotions.promoTypes.${type}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label>{t('geoPromotions.discountValue')}</Label>
                  <Input type="number" value={form.discount_value || ''} onChange={e => setForm(prev => ({ ...prev, discount_value: e.target.value ? Number(e.target.value) : undefined }))} />
                </div>
                <div className="w-28">
                  <Label>{t('geoPromotions.discountUnit')}</Label>
                  <Select value={form.discount_unit || 'percent'} onValueChange={v => setForm(prev => ({ ...prev, discount_unit: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">%</SelectItem>
                      <SelectItem value="fixed">$</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="md:col-span-2">
                <GeoPromotionLocationList
                  locations={form.locations}
                  onChange={(locations) => setForm(prev => ({ ...prev, locations }))}
                />
              </div>
              <div>
                <Label>{t('geoPromotions.maxNotifications')}</Label>
                <Input type="number" value={form.max_notifications || ''} onChange={e => setForm(prev => ({ ...prev, max_notifications: e.target.value ? Number(e.target.value) : undefined }))} placeholder={t('geoPromotions.unlimited')} />
              </div>
              <div>
                <Label>{t('geoPromotions.startDate')} *</Label>
                <Input type="datetime-local" value={form.starts_at} onChange={e => setForm(prev => ({ ...prev, starts_at: e.target.value }))} />
              </div>
              <div>
                <Label>{t('geoPromotions.endDate')} *</Label>
                <Input type="datetime-local" value={form.ends_at} onChange={e => setForm(prev => ({ ...prev, ends_at: e.target.value }))} />
              </div>
              <div className="md:col-span-2">
                <Label>{t('geoPromotions.targetSpecies')}</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {SPECIES_OPTIONS.map(species => (
                    <Badge
                      key={species}
                      variant={form.target_species.includes(species) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => handleSpeciesToggle(species)}
                    >
                      {t(`species.${species}`, species)}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2 flex gap-2 pt-4">
                <Button onClick={handleSubmit} disabled={isCreating || isUpdating} className="flex-1">
                  {editId ? t('common.save') : t('geoPromotions.create')}
                </Button>
                <Button variant="outline" onClick={() => { setShowForm(false); setEditId(null); setForm({ ...emptyForm }); }} className="flex-1">
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Button variant="outline" onClick={handlePrint}><Printer className="h-4 w-4 mr-2" />{t('geoPromotions.print')}</Button>
        <Button variant="outline" onClick={handleExportCSV}><Download className="h-4 w-4 mr-2" />CSV</Button>
        <Button variant="outline" onClick={handleExportExcel}><FileSpreadsheet className="h-4 w-4 mr-2" />Excel</Button>
      </div>

      {/* Promotions Table */}
      <Card className="print:shadow-none print:border print:break-inside-avoid">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            {t('geoPromotions.promotionsList')}
            <Badge variant="outline">{promotions.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {promotions.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('geoPromotions.noPromotions')}</h3>
              <p className="text-muted-foreground">{t('geoPromotions.noPromotionsDesc')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('geoPromotions.title')}</TableHead>
                    <TableHead>{t('geoPromotions.type')}</TableHead>
                    <TableHead>{t('geoPromotions.business')}</TableHead>
                    <TableHead>{t('geoPromotions.discount')}</TableHead>
                    <TableHead>{t('geoPromotions.locations', 'Locations')}</TableHead>
                    <TableHead>{t('geoPromotions.dates')}</TableHead>
                    <TableHead>{t('geoPromotions.status')}</TableHead>
                    <TableHead>{t('geoPromotions.notificationsSent')}</TableHead>
                    <TableHead>{t('geoPromotions.clicks')}</TableHead>
                    <TableHead className="print:hidden">{t('geoPromotions.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {promotions.map(promo => {
                    const status = getStatus(promo);
                    const stats = impressionStats[promo.id] || { sent: 0, clicked: 0 };
                    return (
                      <TableRow key={promo.id} className="print:break-inside-avoid">
                        <TableCell className="font-medium">{promo.title}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{t(`geoPromotions.promoTypes.${promo.promo_type}`, promo.promo_type)}</Badge>
                        </TableCell>
                        <TableCell>{promo.business_name}</TableCell>
                        <TableCell>
                          {promo.discount_value ? `${promo.discount_value}${promo.discount_unit === 'percent' ? '%' : '$'}` : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            <MapPin className="h-3 w-3 mr-1" />
                            {promo.locations?.length || 1}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">
                          <div>{format(new Date(promo.starts_at), 'MMM dd, yyyy')}</div>
                          <div className="text-muted-foreground">{format(new Date(promo.ends_at), 'MMM dd, yyyy')}</div>
                        </TableCell>
                        <TableCell>{getStatusBadge(status)}</TableCell>
                        <TableCell>{promo.current_notifications}</TableCell>
                        <TableCell>{stats.clicked}</TableCell>
                        <TableCell className="print:hidden">
                          <div className="flex gap-1">
                            <Switch
                              checked={promo.is_active}
                              onCheckedChange={(checked) => toggleActive({ id: promo.id, is_active: checked })}
                            />
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(promo)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(promo.id)} disabled={isDeleting}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminGeoPromotions;
